import React, { useEffect, useMemo, useState } from 'react';
import { useBasketStore, getCurrentCatalogId } from '../state/basketStore';
import { createCatalogOrder, getCartPreview } from '../lib/api';
import { getOrderSource, getWebsiteId, getOrgId, getStoreId } from '../utils/tenantBoot';

/**
 * Floating Basket Component
 * Shows basket count badge, expands to show basket contents
 * Handles Send to Till flow
 */
export default function FloatingBasket({ onToggle, showBasket, onOrderComplete }) {
  // Subscribe to the stable raw items array; filter outside the selector so the
  // snapshot reference only changes when basket state actually changes (fixes render loop)
  const rawItems = useBasketStore(state => state.items);
  const items = useMemo(() => {
    const catalogId = getCurrentCatalogId();
    return catalogId
      ? rawItems.filter(item => item.catalog_id === catalogId)
      : rawItems.filter(item => item.catalog_id === 'default');
  }, [rawItems]);
  const getTotalItems = useBasketStore(state => state.getTotalItems);
  const getSubtotal = useBasketStore(state => state.getSubtotal);
  const getPromotionDiscounts = useBasketStore(state => state.getPromotionDiscounts);
  const getTotalDiscount = useBasketStore(state => state.getTotalDiscount);
  const getTotal = useBasketStore(state => state.getTotal);
  const updateQuantity = useBasketStore(state => state.updateQuantity);
  const removeItem = useBasketStore(state => state.removeItem);
  const toBackendFormat = useBasketStore(state => state.toBackendFormat);

  const [mobileNumber, setMobileNumber] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [livePreview, setLivePreview] = useState(null);

  const totalItems = getTotalItems();
  const localSubtotal = getSubtotal();
  const localPromotionDiscounts = getPromotionDiscounts();
  const localTotalDiscount = getTotalDiscount();
  const localFinalTotal = getTotal();

  const backendItems = useMemo(() => toBackendFormat(), [items, toBackendFormat]);

  useEffect(() => {
    let cancelled = false;

    if (!backendItems.length) {
      setLivePreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const preview = await getCartPreview(backendItems, getOrgId(), getStoreId());
        if (!cancelled) {
          setLivePreview(preview);
        }
      } catch (err) {
        console.warn('[FloatingBasket] Live cart preview failed:', err);
        if (!cancelled) {
          setLivePreview(null);
        }
      }
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [backendItems]);

  const subtotal = livePreview?.subtotal ?? localSubtotal;
  const totalDiscount = livePreview?.total_discount ?? localTotalDiscount;
  const finalTotal = livePreview?.total ?? localFinalTotal;
  const promotionDiscounts = livePreview
    ? (() => {
        const fromSummary = (livePreview.promotions || [])
          .map((promo) => ({
            name: promo.promo_name || 'Promotion',
            amount: Number(promo.discount_amount || 0),
            sets: 1,
          }))
          .filter((promo) => promo.amount > 0);

        if (fromSummary.length > 0) {
          return fromSummary;
        }

        const fallbackDiscount = Number(livePreview.total_discount || 0);
        return fallbackDiscount > 0
          ? [{ name: 'Promotions', amount: fallbackDiscount, sets: 1 }]
          : [];
      })()
    : localPromotionDiscounts;

  const handleSendToTill = async () => {
    if (items.length === 0) {
      setError('Basket is empty');
      return;
    }

    try {
      setValidating(true);
      setSending(true);
      setError(null);

      const sendItems = toBackendFormat();
      
      // Validate with server first
      try {
        const preview = await getCartPreview(sendItems, getOrgId(), getStoreId());
        const priceDifference = Math.abs(preview.total - finalTotal);
        
        if (priceDifference >= 0.01) {
          // Prices differ - show error and stop
          setError(`Price updated: Was £${finalTotal.toFixed(2)}, now £${preview.total.toFixed(2)}. Please review basket.`);
          setValidating(false);
          setSending(false);
          return;
        }
      } catch (validationErr) {
        console.warn('Cart validation failed, proceeding anyway:', validationErr);
        // Continue with order even if validation fails
      }

      // Get configuration from domain/URL
      const orgId = getOrgId();
      const storeId = getStoreId();
      const orderSource = getOrderSource(); // 'kiosk' or 'website'
      const websiteId = getWebsiteId(); // domain identifier

      // Create order
      const response = await createCatalogOrder(
        sendItems, 
        mobileNumber || null,
        orgId,
        storeId,
        orderSource,
        websiteId
      );
      
      if (response && response.code) {
        onOrderComplete(response.code);
        setMobileNumber(''); // Reset
      } else {
        throw new Error('No order code received');
      }
    } catch (err) {
      console.error('Failed to create order:', err);
      setError(err.message || 'Failed to send order to till');
    } finally {
      setSending(false);
      setValidating(false);
    }
  };

  return (
    <>
      {/* Floating Basket Button */}
      <button
        className={`floating-basket-btn ${showBasket ? 'active' : ''}`}
        onClick={onToggle}
        aria-label="View basket"
      >
        <span className="basket-icon">🛒</span>
        {totalItems > 0 && (
          <>
            <span className="basket-badge">{totalItems}</span>
            <span className="basket-total">£{finalTotal.toFixed(2)}</span>
          </>
        )}
      </button>

      {/* Basket Panel */}
      {showBasket && (
        <div className="basket-panel">
          <div className="basket-panel-overlay" onClick={onToggle} />
          <div className="basket-panel-content">
            <div className="basket-panel-header">
              <h2>Your Basket</h2>
              <button className="basket-close" onClick={onToggle}>
                ✕
              </button>
            </div>

            {items.length === 0 ? (
              <div className="basket-empty">
                <div className="basket-empty-icon">🛒</div>
                <p>Your basket is empty</p>
                <p className="basket-empty-hint">Tap products to add them</p>
              </div>
            ) : (
              <>
                <div className="basket-items">
                  {items.map((item) => (
                    <div key={item.itemId} className="basket-item">
                      {item.image_url && (
                        <img src={item.image_url} alt="" className="basket-item-img" />
                      )}
                      <div className="basket-item-info">
                        <div className="basket-item-name">{item.product_name}</div>
                        <div className="basket-item-price">
                          £{item.price.toFixed(2)} × {item.qty}
                        </div>
                      </div>

                      <div className="basket-item-actions">
                        <div className="basket-qty-controls">
                          <button
                            className="basket-qty-btn"
                            onClick={() => updateQuantity(item.itemId, item.qty - 1)}
                          >
                            −
                          </button>
                          <span className="basket-qty">{item.qty}</span>
                          <button
                            className="basket-qty-btn"
                            onClick={() => updateQuantity(item.itemId, item.qty + 1)}
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="basket-remove-btn"
                          onClick={() => removeItem(item.itemId)}
                          aria-label="Remove item"
                        >
                          🗑️
                        </button>
                      </div>

                      <div className="basket-item-total">
                        £{(item.price * item.qty).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="basket-footer">
                  <div className="basket-subtotal">
                    <span>Subtotal:</span>
                    <span className="basket-subtotal-amount">
                      £{subtotal.toFixed(2)}
                    </span>
                  </div>

                  {/* Promotion Discounts */}
                  {promotionDiscounts.length > 0 && (
                    <>
                      {promotionDiscounts.map((discount, idx) => (
                        <div key={idx} className="basket-discount">
                          <span style={{ color: '#dc2626', fontSize: '14px' }}>
                            🎉 {discount.name} {discount.sets > 1 ? `(×${discount.sets})` : ''}
                          </span>
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>
                            -£{discount.amount.toFixed(2)}
                          </span>
                        </div>
                      ))}
                      <div className="basket-total-discount">
                        <span>Total Savings:</span>
                        <span style={{ color: '#dc2626', fontWeight: 700 }}>
                          -£{totalDiscount.toFixed(2)}
                        </span>
                      </div>
                      <div className="basket-final-total">
                        <span>Total:</span>
                        <span className="basket-final-total-amount">
                          £{finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}

                  <input
                    type="tel"
                    className="basket-mobile-input"
                    placeholder="Mobile number (optional)"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    maxLength={15}
                  />

                  {error && (
                    <div className="basket-error">
                      {error}
                    </div>
                  )}

                  <button
                    className="basket-send-btn"
                    onClick={handleSendToTill}
                    disabled={sending || validating || items.length === 0}
                  >
                    {validating ? '🔄 Validating...' : sending ? 'Sending...' : '📤 Send to Till'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
